import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import apiClient from '../../magpiie-client/client';
import { apiErrorMessage } from './apiError';
import type {
  BoardTask,
  CreateBoardTask,
  CreateTaskBoard,
  CreateTaskStage,
  CreateTaskStatus,
  UpdateBoardTask,
  UpdateTaskBoard,
  UpdateTaskStage,
  UpdateTaskStatus,
} from '../types/taskboard';

/* -------------------------------------------------------------------------- */
/* Boards                                                                     */
/* -------------------------------------------------------------------------- */

export const fetchBoards = createAsyncThunk(
  'taskboard/fetchBoards',
  async (channelId: number, { rejectWithValue }) => {
    const { data, error } = await apiClient.GET(
      '/v2/channel/{channelId}/task-board',
      { params: { path: { channelId } } }
    );
    if (error) return rejectWithValue('Failed to fetch task boards');
    return { channelId, boards: data ?? [] };
  }
);

export const createBoard = createAsyncThunk(
  'taskboard/createBoard',
  async (
    { channelId, dto }: { channelId: number; dto: CreateTaskBoard },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.POST(
      '/v2/channel/{channelId}/task-board',
      { params: { path: { channelId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to create task board');
    return { channelId, board: data };
  }
);

export const updateBoard = createAsyncThunk(
  'taskboard/updateBoard',
  async (
    {
      channelId,
      boardId,
      dto,
    }: { channelId: number; boardId: number; dto: UpdateTaskBoard },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{id}',
      { params: { path: { channelId, id: boardId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to update task board');
    return { channelId, board: data };
  }
);

export const deleteBoard = createAsyncThunk(
  'taskboard/deleteBoard',
  async (
    { channelId, boardId }: { channelId: number; boardId: number },
    { rejectWithValue }
  ) => {
    const { error } = await apiClient.DELETE(
      '/v2/channel/{channelId}/task-board/{id}',
      { params: { path: { channelId, id: boardId } } }
    );
    if (error) return rejectWithValue('Failed to delete task board');
    return { channelId, boardId };
  }
);

/* -------------------------------------------------------------------------- */
/* Board templates                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Every template this channel can see: the system presets (Talking Head /
 * Faceless / Custom / Posts board) plus templates the channel has saved
 * itself. Read is granted to every role including VIEWER, so this is safe to
 * fetch on any board-creation surface.
 */
export const fetchBoardTemplates = createAsyncThunk(
  'taskboard/fetchBoardTemplates',
  async (channelId: number, { rejectWithValue }) => {
    const { data, error } = await apiClient.GET(
      '/v2/channel/{channelId}/task-board-template',
      { params: { path: { channelId } } }
    );
    if (error) return rejectWithValue('Failed to fetch board templates');
    return { channelId, templates: data ?? [] };
  }
);

/* -------------------------------------------------------------------------- */
/* Stages (STAGED boards only)                                                */
/* -------------------------------------------------------------------------- */

export const fetchStages = createAsyncThunk(
  'taskboard/fetchStages',
  async (
    { channelId, boardId }: { channelId: number; boardId: number },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.GET(
      '/v2/channel/{channelId}/task-board/{boardId}/stages',
      { params: { path: { channelId, boardId } } }
    );
    if (error) return rejectWithValue('Failed to fetch stages');
    return { boardId, stages: data ?? [] };
  }
);

export const createStage = createAsyncThunk(
  'taskboard/createStage',
  async (
    {
      channelId,
      boardId,
      dto,
    }: { channelId: number; boardId: number; dto: CreateTaskStage },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.POST(
      '/v2/channel/{channelId}/task-board/{boardId}/stages',
      { params: { path: { channelId, boardId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to create stage');
    return { boardId, stage: data };
  }
);

export const updateStage = createAsyncThunk(
  'taskboard/updateStage',
  async (
    {
      channelId,
      boardId,
      stageId,
      dto,
    }: {
      channelId: number;
      boardId: number;
      stageId: number;
      dto: UpdateTaskStage;
    },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/stages/{stageId}',
      { params: { path: { channelId, boardId, stageId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to update stage');
    return { boardId, stage: data };
  }
);

export const deleteStage = createAsyncThunk(
  'taskboard/deleteStage',
  async (
    {
      channelId,
      boardId,
      stageId,
    }: { channelId: number; boardId: number; stageId: number },
    { rejectWithValue }
  ) => {
    const { error } = await apiClient.DELETE(
      '/v2/channel/{channelId}/task-board/{boardId}/stages/{stageId}',
      { params: { path: { channelId, boardId, stageId } } }
    );
    if (error) return rejectWithValue('Failed to delete stage');
    return { boardId, stageId };
  }
);

export const reorderStages = createAsyncThunk(
  'taskboard/reorderStages',
  async (
    {
      channelId,
      boardId,
      stageIds,
    }: { channelId: number; boardId: number; stageIds: number[] },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/stages/reorder',
      {
        params: { path: { channelId, boardId } },
        body: { stage_ids: stageIds },
      }
    );
    if (error) return rejectWithValue('Failed to reorder stages');
    return { boardId, stages: data ?? [] };
  }
);

/* -------------------------------------------------------------------------- */
/* Statuses (KANBAN boards only)                                              */
/* -------------------------------------------------------------------------- */

export const fetchStatuses = createAsyncThunk(
  'taskboard/fetchStatuses',
  async (
    { channelId, boardId }: { channelId: number; boardId: number },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.GET(
      '/v2/channel/{channelId}/task-board/{boardId}/statuses',
      { params: { path: { channelId, boardId } } }
    );
    if (error) return rejectWithValue('Failed to fetch statuses');
    return { boardId, statuses: data ?? [] };
  }
);

export const createStatus = createAsyncThunk(
  'taskboard/createStatus',
  async (
    {
      channelId,
      boardId,
      dto,
    }: { channelId: number; boardId: number; dto: CreateTaskStatus },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.POST(
      '/v2/channel/{channelId}/task-board/{boardId}/statuses',
      { params: { path: { channelId, boardId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to create status');
    return { boardId, status: data };
  }
);

export const updateStatus = createAsyncThunk(
  'taskboard/updateStatus',
  async (
    {
      channelId,
      boardId,
      statusId,
      dto,
    }: {
      channelId: number;
      boardId: number;
      statusId: number;
      dto: UpdateTaskStatus;
    },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/statuses/{statusId}',
      { params: { path: { channelId, boardId, statusId } }, body: dto }
    );
    if (error) return rejectWithValue('Failed to update status');
    return { boardId, status: data };
  }
);

export const deleteStatus = createAsyncThunk(
  'taskboard/deleteStatus',
  async (
    {
      channelId,
      boardId,
      statusId,
    }: { channelId: number; boardId: number; statusId: number },
    { rejectWithValue }
  ) => {
    const { error } = await apiClient.DELETE(
      '/v2/channel/{channelId}/task-board/{boardId}/statuses/{statusId}',
      { params: { path: { channelId, boardId, statusId } } }
    );
    if (error) return rejectWithValue('Failed to delete status');
    return { boardId, statusId };
  }
);

export const reorderStatuses = createAsyncThunk(
  'taskboard/reorderStatuses',
  async (
    {
      channelId,
      boardId,
      statusIds,
    }: { channelId: number; boardId: number; statusIds: number[] },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/statuses/reorder',
      {
        params: { path: { channelId, boardId } },
        body: { status_ids: statusIds },
      }
    );
    if (error) return rejectWithValue('Failed to reorder statuses');
    return { boardId, statuses: data ?? [] };
  }
);

/* -------------------------------------------------------------------------- */
/* Board tasks                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Page a board's task list to exhaustion and return every row.
 *
 * The list endpoint is paginated (`{ page, count, data }`) and `limit` defaults
 * to 10 server-side, so a board with more tasks than that would silently render
 * a partial column set. 50 is the server's `@Max(50)` cap.
 *
 * Shared by the active-board fetch and the archive fetch so the "stop on a
 * short page as well as on the count" guard below is written once — a
 * miscounted total can't spin either caller's loop.
 *
 * ponytail: serial fetch-all, so a board of N tasks costs ceil(N/50)
 * round-trips before the board paints — fine at the tens-of-tasks a
 * channel board actually holds, wrong at thousands. The board renders
 * every card too, so the real fix when that day comes is windowing plus
 * per-column lazy pages, not just parallelising this loop.
 */
const fetchAllBoardTasks = async (
  channelId: number,
  boardId: number,
  query: {
    stage_id?: number;
    status_id?: number;
    archived?: boolean;
    q?: string;
  }
): Promise<{ tasks: BoardTask[]; error?: unknown }> => {
  const PAGE_SIZE = 50;
  const tasks: BoardTask[] = [];

  for (let page = 1; ; page += 1) {
    const { data, error } = await apiClient.GET(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks',
      {
        params: {
          path: { channelId, boardId },
          query: { ...query, page, limit: PAGE_SIZE },
        },
      }
    );
    if (error) return { tasks, error };

    tasks.push(...(data?.data ?? []));

    if (
      !data ||
      data.data.length < PAGE_SIZE ||
      tasks.length >= (data.count ?? tasks.length)
    ) {
      break;
    }
  }

  return { tasks };
};

export const fetchBoardTasks = createAsyncThunk(
  'taskboard/fetchBoardTasks',
  async (
    {
      channelId,
      boardId,
      stageId,
      statusId,
    }: {
      channelId: number;
      boardId: number;
      stageId?: number;
      statusId?: number;
    },
    { rejectWithValue }
  ) => {
    const { tasks, error } = await fetchAllBoardTasks(channelId, boardId, {
      stage_id: stageId,
      status_id: statusId,
    });
    if (error) return rejectWithValue('Failed to fetch tasks');

    return { boardId, tasks };
  }
);

/**
 * Archived tasks for a board — the same list endpoint with `archived=true`,
 * which flips it from "active only" to "archived only".
 *
 * `q` is passed to the server rather than filtered client-side: an archive
 * grows without bound, so narrowing before the wire is the point.
 */
export const fetchArchivedTasks = createAsyncThunk(
  'taskboard/fetchArchivedTasks',
  async (
    {
      channelId,
      boardId,
      q,
    }: { channelId: number; boardId: number; q?: string },
    { rejectWithValue }
  ) => {
    const { tasks, error } = await fetchAllBoardTasks(channelId, boardId, {
      archived: true,
      q: q?.trim() || undefined,
    });
    if (error) return rejectWithValue('Failed to fetch archived tasks');

    return { boardId, tasks };
  }
);

export const createBoardTask = createAsyncThunk(
  'taskboard/createBoardTask',
  async (
    {
      channelId,
      boardId,
      dto,
    }: { channelId: number; boardId: number; dto: CreateBoardTask },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.POST(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks',
      { params: { path: { channelId, boardId } }, body: dto }
    );
    if (error)
      return rejectWithValue(apiErrorMessage(error, 'Failed to create task'));
    return { boardId, task: data };
  }
);

export const updateBoardTask = createAsyncThunk(
  'taskboard/updateBoardTask',
  async (
    {
      channelId,
      boardId,
      taskId,
      dto,
    }: {
      channelId: number;
      boardId: number;
      taskId: number;
      dto: UpdateBoardTask;
    },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks/{taskId}',
      { params: { path: { channelId, boardId, taskId } }, body: dto }
    );
    if (error)
      return rejectWithValue(apiErrorMessage(error, 'Failed to update task'));
    return { boardId, task: data };
  }
);

export const deleteBoardTask = createAsyncThunk(
  'taskboard/deleteBoardTask',
  async (
    {
      channelId,
      boardId,
      taskId,
    }: { channelId: number; boardId: number; taskId: number },
    { rejectWithValue }
  ) => {
    const { error } = await apiClient.DELETE(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks/{taskId}',
      { params: { path: { channelId, boardId, taskId } } }
    );
    if (error)
      return rejectWithValue(apiErrorMessage(error, 'Failed to delete task'));
    return { boardId, taskId };
  }
);

/**
 * Archive a task (soft delete — stamps `archived_at` server-side).
 *
 * Not optimistic, unlike the drag-driven column move: archiving is a
 * deliberate menu action where a brief wait is fine, and a silent rollback
 * after the card had already vanished would read as the action failing
 * silently. The Undo affordance lives in the toast instead.
 */
export const archiveBoardTask = createAsyncThunk(
  'taskboard/archiveBoardTask',
  async (
    {
      channelId,
      boardId,
      taskId,
    }: { channelId: number; boardId: number; taskId: number },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks/{taskId}/archive',
      { params: { path: { channelId, boardId, taskId } } }
    );
    if (error || !data) {
      return rejectWithValue(apiErrorMessage(error, 'Failed to archive task'));
    }
    return { boardId, task: data };
  }
);

/** Restore an archived task back onto the active board. */
export const restoreBoardTask = createAsyncThunk(
  'taskboard/restoreBoardTask',
  async (
    {
      channelId,
      boardId,
      taskId,
    }: { channelId: number; boardId: number; taskId: number },
    { rejectWithValue }
  ) => {
    const { data, error } = await apiClient.PATCH(
      '/v2/channel/{channelId}/task-board/{boardId}/tasks/{taskId}/restore',
      { params: { path: { channelId, boardId, taskId } } }
    );
    if (error || !data) {
      return rejectWithValue(apiErrorMessage(error, 'Failed to restore task'));
    }
    return { boardId, task: data };
  }
);

/* -------------------------------------------------------------------------- */
/* UI state                                                                   */
/* -------------------------------------------------------------------------- */

export const setActiveBoard = createAction<number | null>(
  'taskboard/setActiveBoard'
);
